import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';


const quickActions = [
  {
    title: "Questions fréquentes",
    description: "Consultez notre FAQ pour des réponses rapides",
    icon: "fas fa-question-circle",
    link: "/faq",
    color: "blue"
  },
  {
    title: "Nous contacter",
    description: "Contactez notre équipe support",
    icon: "fas fa-envelope",
    link: "/contact",
    color: "green"
  },
  {
    title: "Signaler un bug",
    description: "Aidez-nous à améliorer la plateforme",
    icon: "fas fa-bug",
    link: "/contact",
    color: "red"
  }
];

export const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<'help' | 'how-it-works'>('help');
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({});

  const toggleAccordion = (id: string) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Aide & Fonctionnement
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Tout ce que vous devez savoir pour utiliser TutorMatch HEG efficacement
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex justify-center space-x-1 bg-gray-100 p-1 rounded-lg max-w-md mx-auto">
            <button 
              onClick={() => setActiveSection('how-it-works')}
              className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all ${
                activeSection === 'how-it-works' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
              }`}
            >
              <i className="fas fa-play-circle mr-2"></i>
              Comment ça marche
            </button>
            <button 
              onClick={() => setActiveSection('help')}
              className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all ${
                activeSection === 'help' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
              }`}
            >
              <i className="fas fa-life-ring mr-2"></i>
              Centre d'aide
            </button>
          </div>
        </div>
      </section>

      {/* Contenu selon l'onglet actif */}
      {activeSection === 'how-it-works' ? (
        // Section Comment ça marche
        <div>
          {/* Pour les étudiant-es */}
          <section className="py-16">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <i className="fas fa-user-graduate text-2xl text-blue-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Trouver un-e tuteur-trice
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  En quelques clics, trouvez le-la tuteur-trice parfait-e pour vos besoins
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {/* Étape 1 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    1
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Parcourir ou filtrer
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Explorez les tuteur-trices disponibles ou utilisez la 
                    <strong> recherche avancée</strong> pour filtrer par filière, matière, disponibilités et tarif.
                  </p>
                  <button
                    onClick={() => navigate('/filter')}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <i className="fas fa-search mr-1"></i>
                    Essayer la recherche
                  </button>
                </div>

                {/* Étape 2 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    2
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Instant Connect
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Cliquez sur <strong>"Instant Connect"</strong> sur les profils qui vous intéressent. 
                    Si le-la tuteur-trice vous like en retour, c'est un match !
                  </p>
                </div>

                {/* Étape 3 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    3
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Échangez et planifiez
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Après un match, échangez vos coordonnées et organisez votre première session. 
                    Le paiement se fait <strong>directement entre vous</strong>.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pour les tuteur-trices */}
          <section className="py-16 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <i className="fas fa-chalkboard-teacher text-2xl text-green-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Devenir tuteur-trice
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Partagez vos connaissances et gagnez de l'argent en aidant d'autres étudiant-es
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {/* Étape 1 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    1
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Créez votre profil
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Inscrivez-vous comme tuteur-trice et complétez votre profil avec vos 
                    <strong> compétences, tarifs et disponibilités</strong>.
                  </p>
                  <button
                    onClick={() => navigate('/become-tutor')}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    <i className="fas fa-user-plus mr-1"></i>
                    Créer mon profil
                  </button>
                </div>

                {/* Étape 2 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    2
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Recevez des demandes
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Votre profil apparaît dans les résultats de recherche des étudiant-es. 
                    Vous recevez des notifications quand on vous like.
                  </p>
                </div>

                {/* Étape 3 */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    3
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Enseignez et gagnez
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Acceptez les demandes qui vous conviennent et commencez à enseigner. 
                    Vous fixez <strong>vos propres tarifs</strong> et horaires.
                  </p>
                </div>
              </div>

              <div className="text-center mt-12">
                <button
                  onClick={() => navigate('/filter')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <i className="fas fa-search mr-2"></i>
                  Commencer ma recherche
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : (
        // Section Centre d'aide avec contenu détaillé
        <div>
          {/* Quick Actions */}
          <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                Actions rapides
              </h2>
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  {quickActions.map((action, index) => (
                    <Link
                      key={index}
                      to={action.link}
                    className="block bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow group"
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                      action.color === 'blue' ? 'bg-blue-100' :
                      action.color === 'green' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <i className={`${action.icon} text-2xl ${
                        action.color === 'blue' ? 'text-blue-600' :
                        action.color === 'green' ? 'text-green-600' : 'text-red-600'
                      }`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {action.title}
                    </h3>
                    <p className="text-gray-600">
                      {action.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Sections d'aide détaillées */}
          
          {/* Premiers pas */}
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                  <i className="fas fa-play-circle text-blue-600 mr-3"></i>
                  Premiers pas
                </h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div id="compte" className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="fas fa-user-plus text-blue-600 mr-2"></i>
                      Créer votre compte
                    </h3>
                    <div className="space-y-3 text-gray-600">
                      <p>1. Cliquez sur "S'inscrire" dans le header</p>
                      <p>2. Choisissez votre rôle : étudiant-e ou tuteur-trice</p>
                      <p>3. Remplissez vos informations de base</p>
                      <p>4. Vérifiez votre email pour activer votre compte</p>
                    </div>
                  </div>

                  <div id="profil" className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="fas fa-user-edit text-green-600 mr-2"></i>
                      Compléter votre profil
                    </h3>
                    <div className="space-y-3 text-gray-600">
                      <p>1. Renseignez votre filière et année d'études</p>
                      <p>2. Sélectionnez vos matières de prédilection</p>
                      <p>3. Définissez vos disponibilités</p>
                      <p>4. Fixez votre tarif horaire (pour les tuteur-trices)</p>
                      <div className="mt-4 p-3 bg-green-50 rounded">
                        <p className="text-green-800 text-sm">
                          <i className="fas fa-star mr-1"></i>
                          <strong>Astuce :</strong> Un profil complet augmente vos chances de matching !
                        </p>
                      </div>
                    </div>
                  </div>

                  <div id="navigation" className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="fas fa-compass text-purple-600 mr-2"></i>
                      Naviguer sur la plateforme
                    </h3>
                    <div className="space-y-3 text-gray-600">
                      <p><strong>Accueil :</strong> Recherche rapide et présentation</p>
                      <p><strong>Tuteur-trices :</strong> Parcourir tous les profils</p>
                      <p><strong>Recherche avancée :</strong> Filtres détaillés</p>
                      <p><strong>Mon profil :</strong> Gestion de votre compte</p>
                      <div className="mt-4 p-3 bg-purple-50 rounded">
                        <p className="text-purple-800 text-sm">
                          <i className="fas fa-heart mr-1"></i>
                          <strong>Notifications :</strong> L'icône cœur indique vos demandes en cours.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div id="confidentialite" className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      <i className="fas fa-shield-alt text-red-600 mr-2"></i>
                      Paramètres de confidentialité
                    </h3>
                    <div className="space-y-3 text-gray-600">
                      <p>• Vos informations de contact restent privées</p>
                      <p>• Seuls les matches mutuels révèlent les coordonnées</p>
                      <p>• Vous pouvez supprimer votre profil à tout moment</p>
                      <p>• Toutes les données sont conformes RGPD (UE) et LPD (Suisse)</p>
                      <div className="mt-4 p-3 bg-red-50 rounded">
                        <p className="text-red-800 text-sm">
                          <i className="fas fa-lock mr-1"></i>
                          <strong>Sécurité :</strong> Vos données ne sont jamais vendues ou partagées, conforme RGPD et LPD.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Guide étudiant-es - Accordéons */}
          <section className="py-16 bg-white">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                  <i className="fas fa-user-graduate text-green-600 mr-3"></i>
                  Guide pour les étudiant-es
                </h2>
                
                <div className="space-y-4">
                  {/* Accordéon 1 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('trouver-tuteur')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-search text-green-600 mr-3"></i>
                        Comment trouver un-e tuteur-trice
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['trouver-tuteur'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['trouver-tuteur'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p><strong>1. Recherche simple :</strong> Parcourez la liste des tuteur-trices</p>
                          <p><strong>2. Filtres :</strong> Utilisez les critères (filière, tarif, disponibilités)</p>
                          <p><strong>3. Profils :</strong> Consultez les évaluations et matières</p>
                          <p><strong>4. Contact :</strong> Cliquez sur "Instant Connect"</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accordéon 2 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('filtres')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-filter text-green-600 mr-3"></i>
                        Utiliser les filtres de recherche
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['filtres'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['filtres'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p><strong>Filière :</strong> Trouvez des tuteur-trices de votre domaine</p>
                          <p><strong>Matières :</strong> Recherche par matière spécifique</p>
                          <p><strong>Tarif :</strong> Définissez votre budget maximum</p>
                          <p><strong>Disponibilités :</strong> Sélectionnez vos créneaux libres</p>
                          <p><strong>Langue :</strong> Français ou anglais</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accordéon 3 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('instant-connect')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-bolt text-green-600 mr-3"></i>
                        Instant Connect
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['instant-connect'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['instant-connect'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p>Le bouton "Instant Connect" permet de liker un profil rapidement :</p>
                          <p>• <strong>Vous likez :</strong> Le-la tuteur-trice est notifié-e</p>
                          <p>• <strong>Match mutuel :</strong> Échange des coordonnées</p>
                          <p>• <strong>Pas de match :</strong> Votre demande reste en attente</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accordéon 4 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('demandes')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-heart text-green-600 mr-3"></i>
                        Gérer vos demandes
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['demandes'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['demandes'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p>Accédez à vos demandes via l'icône ❤️ dans le header :</p>
                          <p>• <strong>En attente :</strong> Demandes envoyées non répondues</p>
                          <p>• <strong>Matches :</strong> Demandes acceptées avec coordonnées</p>
                          <p>• <strong>Notifications :</strong> Badge rouge pour nouvelles réponses</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Guide tuteur-trices - Accordéons */}
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                  <i className="fas fa-chalkboard-teacher text-purple-600 mr-3"></i>
                  Guide pour les tuteur-trices
                </h2>
                
                <div className="space-y-4">
                  {/* Accordéon Tuteur 1 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('devenir-tuteur')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-user-graduate text-purple-600 mr-3"></i>
                        Créer votre profil tuteur-trice
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['devenir-tuteur'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['devenir-tuteur'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p><strong>1. Inscription :</strong> Créez votre compte</p>
                          <p><strong>2. Profil :</strong> Renseignez vos compétences</p>
                          <p><strong>3. Matières :</strong> Sélectionnez vos spécialités</p>
                          <p><strong>4. Disponibilités :</strong> Définissez vos créneaux</p>
                          <p><strong>5. Validation :</strong> Votre profil devient visible</p>
                        </div>
                      </div>
                    )}
                          </div>

                  {/* Accordéon Tuteur 2 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('tarifs')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-euro-sign text-purple-600 mr-3"></i>
                        Définir vos tarifs
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['tarifs'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['tarifs'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p>Vous êtes libre de fixer vos tarifs selon votre expérience et les matières enseignées.</p>
                          <p>Les tarifs varient généralement entre 20 et 50 CHF/heure selon la complexité et votre niveau d'études.</p>
                          <p>Vous pouvez modifier votre tarif à tout moment dans votre profil.</p>
                        </div>
                      </div>
                    )}
                      </div>

                  {/* Accordéon Tuteur 3 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('disponibilites')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-calendar text-purple-600 mr-3"></i>
                        Gérer vos disponibilités
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['disponibilites'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['disponibilites'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p>Indiquez vos créneaux libres pour faciliter les matches :</p>
                          <p>• <strong>Matin :</strong> 8h-12h</p>
                          <p>• <strong>Après-midi :</strong> 12h-18h</p>
                          <p>• <strong>Soir :</strong> 18h-22h</p>
                          <p>Modifiables à tout moment dans votre profil.</p>
                        </div>
                      </div>
                    )}
                    </div>

                  {/* Accordéon Tuteur 4 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('accepter-demandes')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-check-circle text-purple-600 mr-3"></i>
                        Accepter les demandes
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['accepter-demandes'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['accepter-demandes'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-3 text-gray-600 pt-4">
                          <p>Quand un-e étudiant-e vous like :</p>
                          <p>• <strong>Notification :</strong> Badge rouge sur l'icône ❤️</p>
                          <p>• <strong>Profil :</strong> Consultez le profil de l'étudiant-e</p>
                          <p>• <strong>Décision :</strong> Acceptez ou déclinez</p>
                          <p>• <strong>Match :</strong> Échange automatique des coordonnées</p>
                </div>
                      </div>
                    )}
              </div>

                  {/* Accordéon Tuteur 5 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('bonnes-pratiques')}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-gray-900">
                        <i className="fas fa-lightbulb text-purple-600 mr-3"></i>
                        Bonnes pratiques de tutorat
                      </h3>
                      <i className={`fas fa-chevron-down text-gray-400 transition-transform ${openAccordions['bonnes-pratiques'] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openAccordions['bonnes-pratiques'] && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="grid md:grid-cols-2 gap-6 text-gray-600 pt-4">
                          <div>
                            <p className="font-semibold mb-2">Avant la session :</p>
                            <ul className="space-y-1 list-disc list-inside">
                              <li>Clarifiez les objectifs</li>
                              <li>Préparez le matériel nécessaire</li>
                              <li>Confirmez le lieu et l'heure</li>
                              <li>Échangez vos coordonnées</li>
                            </ul>
                          </div>
                          <div>
                            <p className="font-semibold mb-2">Pendant la session :</p>
                            <ul className="space-y-1 list-disc list-inside">
                              <li>Soyez patient-e et bienveillant-e</li>
                              <li>Adaptez votre méthode</li>
                              <li>Encouragez les questions</li>
                              <li>Faites des pauses si nécessaire</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ commune */}
          <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Questions fréquentes
            </h2>

                <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      <i className="fas fa-heart text-red-500 mr-2"></i>
                Comment fonctionne le système de match ?
              </h3>
              <p className="text-gray-600">
                      Comme sur Tinder, mais pour des relations d'aide académique professionnelle ! Vous likez les profils de tuteur-trices qui vous intéressent. 
                      Si la personne vous like en retour, c'est un match et vous pouvez échanger vos coordonnées pour organiser vos sessions de tutorat.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                <i className="fas fa-money-bill-wave text-green-500 mr-2"></i>
                Comment se déroulent les paiements ?
              </h3>
              <p className="text-gray-600">
                      Les paiements se font directement entre l'étudiant-e et le-la tuteur-trice. TutorMatch HEG ne prend aucune commission.
                      Vous négociez librement vos tarifs et modalités (espèces ou TWINT).
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                <i className="fas fa-star text-yellow-500 mr-2"></i>
                À quoi servent les évaluations ?
              </h3>
              <p className="text-gray-600">
                      Seul-es les étudiant-es peuvent noter les tuteur-trices après un match mutuel et une session. 
                      Ces évaluations aident les futur-es étudiant-es à choisir leurs tuteur-trices et améliorent la qualité de la plateforme.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                <i className="fas fa-shield-alt text-red-500 mr-2"></i>
                Mes données sont-elles sécurisées ?
              </h3>
              <p className="text-gray-600">
                      Oui ! Nous respectons le RGPD (UE) et la LPD (Suisse) et ne partageons vos informations qu'après un match mutuel. 
                Vos données de contact restent privées jusqu'à ce que vous décidiez de les partager.
              </p>
                  </div>
            </div>
          </div>
        </div>
      </section>
        </div>
      )}

      {/* CTA Final */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prêt-e à commencer ?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Rejoignez la communauté TutorMatch HEG et découvrez une nouvelle façon 
            d'apprendre et d'enseigner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/filter')}
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <i className="fas fa-search mr-2"></i>
              Trouver un-e tuteur-trice
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors shadow-lg border-2 border-green-600"
            >
              <i className="fas fa-chalkboard-teacher mr-2"></i>
              Devenir tuteur-trice
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
